import { PercentItem } from '@/components/PercentItem';
import {
    ShadowBar,
    ShadowBarChart,
    ShadowBarGroup,
} from '@/components/ShadowBarChart';
import { useConfigContext } from '@/context/configContext';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { useBudgetOptimizationUrl } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationUrl';
import { Typography } from '@analytical-alley/ui';
import moment from 'moment';

function getMonthName(month: number) {
    return moment()
        .month(month - 1)
        .format('MMM');
}

function getFullMonthName(month: number) {
    return moment()
        .month(month - 1)
        .format('MMMM');
}

export const MonthlyMediaInvestment = () => {
    const {
        search: { budget },
    } = useBudgetOptimizationUrl();
    const {
        data: {
            monthlyMediaInvestment,
            optimizationStartDate,
            optimizationEndDate,
            historicDataStartDate,
            historicDataEndDate,
        },
    } = useBudgetOptimizationContext();

    const { formatNumber } = useConfigContext();
    const height = 300;

    const dateRangeDisplay =
        optimizationStartDate && optimizationEndDate
            ? `${moment(optimizationStartDate, 'DD-MM-YYYY').format('MMM YYYY')} - ${moment(optimizationEndDate, 'DD-MM-YYYY').format('MMM YYYY')}`
            : '';

    const historicPeriodDisplay =
        historicDataStartDate && historicDataEndDate
            ? `${moment(historicDataStartDate, 'DD-MM-YYYY').format('MMMM YYYY')} - ${moment(historicDataEndDate, 'DD-MM-YYYY').format('MMMM YYYY')}`
            : '';

    if (!monthlyMediaInvestment) {
        return (
            <div className="glass-tile p-6">
                <Typography
                    variant="titleS"
                    tooltip="Compare your historic monthly media spending against the optimised spending solution."
                >
                    Monthly Media Investment
                </Typography>
                <div className="flex items-center justify-center h-64">
                    <Typography variant="bodyM" className="text-gray-500">
                        No monthly media investment data available
                    </Typography>
                </div>
            </div>
        );
    }

    const { monthlyMediaData, highestValue } = monthlyMediaInvestment;

    return (
        <div className="glass-tile p-6">
            <div className="flex mb-4">
                <div className="flex flex-col">
                    <Typography
                        variant="titleS"
                        tooltip="Compare your historic monthly media spending against the optimised spending solution."
                    >
                        Monthly Media Investment
                    </Typography>
                    {dateRangeDisplay && (
                        <Typography
                            variant="bodyXS"
                            className="text-gray-500 mt-1"
                        >
                            Optimization period: {dateRangeDisplay}
                        </Typography>
                    )}
                    {historicPeriodDisplay && (
                        <Typography
                            variant="bodyXS"
                            className="text-gray-500 mt-1"
                        >
                            Historic data period: {historicPeriodDisplay}
                        </Typography>
                    )}
                </div>
                <span className="grow" />
                <div>
                    <div className="flex items-center">
                        <div className="w-2.5 h-2.5 bg-[#2B67FF] rounded-full mr-1.5 mb-0.5" />
                        <Typography className="m-0" variant="bodyXS">
                            Recommended budget
                        </Typography>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2.5 h-2.5 bg-indigo-300/50 rounded-full mr-1.5 mb-0.5" />
                        <Typography className="m-0" variant="bodyXS">
                            Previous budget
                        </Typography>
                    </div>
                </div>
            </div>

            <div className="relative">
                <div className="flex justify-between">
                    <ShadowBarChart
                        showTickMarks
                        yAxis={'auto'}
                        axisLabelFormatter={formatNumber}
                        height={height}
                        maxValue={highestValue}
                    >
                        <ShadowBarChart.Content>
                            {monthlyMediaData.map(
                                (
                                    {
                                        month,
                                        year,
                                        simulatedBudget,
                                        prevBudget,
                                    },
                                    index,
                                ) => {
                                    const isNegative =
                                        simulatedBudget < prevBudget;
                                    const simulatedBudgetVsActualPercent =
                                        prevBudget
                                            ? Math.round(
                                                  (simulatedBudget /
                                                      prevBudget) *
                                                      100,
                                              )
                                            : 0;
                                    const simulatedBudgetVsActualPercentDisplay =
                                        isNegative
                                            ? -(
                                                  100 -
                                                  simulatedBudgetVsActualPercent
                                              )
                                            : simulatedBudgetVsActualPercent -
                                              100;

                                    const monthName = getMonthName(month);
                                    const fullMonthName = `${getFullMonthName(month)} ${year}`;
                                    return (
                                        <ShadowBarGroup
                                            key={index}
                                            title={monthName}
                                            tooltipContent={
                                                <div className="tooltip-content">
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <td className="pr-2">
                                                                    <Typography className="text-black dark:text-black">
                                                                        Month:
                                                                    </Typography>
                                                                </td>
                                                                <td>
                                                                    <Typography className="text-dark dark:text-dark">
                                                                        {
                                                                            fullMonthName
                                                                        }
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-2">
                                                                    <Typography className="text-black dark:text-black">
                                                                        Recommended
                                                                        media
                                                                        budget:
                                                                    </Typography>
                                                                </td>
                                                                <td>
                                                                    <Typography className="text-dark dark:text-dark">
                                                                        {formatNumber(
                                                                            simulatedBudget,
                                                                        )}
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-2">
                                                                    <Typography className="text-black dark:text-black">
                                                                        Previous
                                                                        media
                                                                        budget:
                                                                    </Typography>
                                                                </td>
                                                                <td>
                                                                    <Typography className="text-dark dark:text-dark">
                                                                        {formatNumber(
                                                                            prevBudget,
                                                                        )}
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-2">
                                                                    <Typography className="text-black dark:text-black">
                                                                        Recommended
                                                                        budget
                                                                        change:
                                                                    </Typography>
                                                                </td>
                                                                <td>
                                                                    <Typography className="text-dark dark:text-dark">
                                                                        {
                                                                            simulatedBudgetVsActualPercentDisplay
                                                                        }
                                                                        %
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="pr-2">
                                                                    <Typography className="text-black dark:text-black">
                                                                        Share of
                                                                        the
                                                                        total
                                                                        budget:
                                                                    </Typography>
                                                                </td>
                                                                <td>
                                                                    <Typography className="text-dark dark:text-dark">
                                                                        {budget
                                                                            ? Math.round(
                                                                                  (simulatedBudget *
                                                                                      100) /
                                                                                      budget,
                                                                              )
                                                                            : ' - '}
                                                                        %
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            }
                                        >
                                            <ShadowBar
                                                height={prevBudget}
                                                variant="secondary"
                                            />
                                            <ShadowBar
                                                height={simulatedBudget}
                                                variant="primary"
                                            >
                                                <ShadowBar.Title>
                                                    <PercentItem
                                                        percent={
                                                            simulatedBudgetVsActualPercentDisplay
                                                        }
                                                    />
                                                </ShadowBar.Title>
                                            </ShadowBar>
                                        </ShadowBarGroup>
                                    );
                                },
                            )}
                        </ShadowBarChart.Content>
                    </ShadowBarChart>
                </div>
            </div>
        </div>
    );
};
